import React from 'react';
import { motion } from 'framer-motion';
import { Layout } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AnimationBoardTargetProps {
    type?: 'specs' | 'connections';
}

export const AnimationBoardTarget: React.FC<AnimationBoardTargetProps> = ({ type }) => {
    const { t } = useTranslation('common');

    return (
        <div className="flex flex-col items-center gap-1 sm:gap-2">
            <div className="w-14 sm:w-20 h-24 sm:h-32 bg-white rounded-xl sm:rounded-2xl border-2 border-accent-primary flex flex-col items-center justify-center relative overflow-hidden shadow-sm">
                <div className="absolute inset-0 bg-accent-primary/[0.03]" />
                <Layout className="w-5 sm:w-7 h-5 sm:h-7 text-accent-primary mb-2 sm:mb-3 relative z-10" />

                <div className="flex items-end gap-0.5 sm:gap-1 h-6 sm:h-10 px-1 sm:px-2 relative z-10">
                    {[0.4, 0.7, 0.5, 0.9, 0.6].map((h, i) => (
                        <motion.div
                            key={i}
                            initial={{ height: 0 }}
                            animate={{ height: `${h * 100}%` }}
                            className="w-1.5 sm:w-2 bg-accent-primary/40 rounded-t-[1px]"
                        />
                    ))}
                </div>

                {/* Feed Indicator Ports */}
                {type === 'specs' && (
                    <>
                        <div className="absolute left-0 top-[30%] w-1.5 h-1 bg-blue-500 rounded-r z-20" />
                        <div className="absolute left-0 bottom-[30%] w-1.5 h-1 bg-emerald-500 rounded-r z-20 shadow-[0_0_4px_#3b82f6]" />
                    </>
                )}

                {type === 'connections' && (
                    <>
                        <div className="absolute left-0 top-1/4 w-1.5 h-1 bg-emerald-500/60 rounded-r z-20" />
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1 bg-amber-500/60 rounded-r z-20" />
                        <div className="absolute left-0 bottom-1/4 w-1.5 h-1 bg-blue-500/60 rounded-r z-20" />
                    </>
                )}
            </div>
            <span className="text-[8px] sm:text-[10px] text-accent-primary font-bold uppercase tracking-widest bg-accent-primary/5 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-accent-primary/10">
                {t('featureExplanation.board.label', 'Board')}
            </span>
        </div>
    );
};
